import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { HandHeart, Building2, MapPinned, Users, Search, CalendarCheck, Award, Mail, ShieldCheck, BadgeCheck, Flag, ArrowRight } from "lucide-react";

// Phase 11 content pass — /about explains (who it's for, how it works, how we
// keep it trustworthy); the landing page sells. Keep the two complementary,
// not repetitive.
const AUDIENCES = [
  { icon: Users, title: "Volunteers", body: "Find causes that match your values, skills and neighbourhood — then watch your hours add up to something real." },
  { icon: Building2, title: "Organisations", body: "Post opportunities, meet matched volunteers, approve hours in one click and show your impact publicly." },
  { icon: MapPinned, title: "Collectives", body: "Neighbourhood groups that organise their own volunteering, gatherings and noticeboards." },
  { icon: HandHeart, title: "Neighbours", body: "Anyone can ask for a hand — a lift to the clinic, help with the shopping — and anyone nearby can offer it." },
];

const STEPS = [
  { icon: Search, title: "Find your cause", body: "Browse or swipe through opportunities matched to your values, skills and location." },
  { icon: CalendarCheck, title: "Show up", body: "Apply in one click, keep everything in your calendar, and message hosts directly." },
  { icon: Award, title: "Watch it count", body: "Log hours, earn badges and certificates, and climb your community leaderboard." },
];

const TRUST_CARDS = [
  { icon: BadgeCheck, title: "Verified organisations", body: "Charities and community groups are independently checked by our team before they carry the verified badge volunteers look for." },
  { icon: ShieldCheck, title: "Safety by design", body: "Neighbour help comes with safety tips at every commitment point, private addresses by default, and messaging that keeps a record." },
  { icon: Flag, title: "Human moderation", body: "Anything can be reported in under a minute, every report is read by a real person, and suspensions can always be appealed." },
];

export default function About() {
  return (
    <div className="bg-cream min-h-screen" data-testid="about-page">
      <Seo title="About" description="CivicHearth turns time into impact — connecting volunteers, organisations, collectives and neighbours across the UK." />

      {/* Mission */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">About CivicHearth</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest mt-4 leading-tight tracking-tight">
            Turn time into impact.
          </h1>
          <p className="text-neutral-600 mt-5 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            CivicHearth connects people who want to help with the causes and neighbours
            who need them — matched by shared values, real skills and the streets you
            actually live on. Every hour you give is recognised, and every ripple adds up.
            We run as a non-profit: free for volunteers, funded by supporters.
          </p>
        </Reveal>
      </section>

      {/* Who it's for */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <Reveal>
          <h2 className="font-display text-2xl text-forest tracking-tight text-center">One hearth, four ways in</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.05}>
              <div data-testid={`about-audience-${a.title.toLowerCase()}`} className="bg-white border border-forest/10 rounded-2xl p-6 h-full">
                <a.icon className="w-7 h-7 text-terracotta" />
                <div className="font-display text-lg text-forest mt-3">{a.title}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <Reveal>
          <h2 className="font-display text-2xl text-forest tracking-tight text-center">How it works</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div data-testid={`about-step-${i + 1}`} className="bg-white border border-forest/10 rounded-2xl p-6 h-full text-center">
                <div className="w-10 h-10 rounded-full bg-forest/5 text-forest font-display flex items-center justify-center mx-auto">{i + 1}</div>
                <s.icon className="w-6 h-6 text-terracotta mx-auto mt-4" />
                <div className="font-display text-lg text-forest mt-2">{s.title}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Trust & safety — explains what the landing only gestures at */}
      <section className="max-w-6xl mx-auto px-6 py-10" data-testid="about-trust-section">
        <Reveal>
          <h2 className="font-display text-2xl text-forest tracking-tight text-center">Built on trust</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {TRUST_CARDS.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.05}>
              <div data-testid={`about-trust-${i + 1}`} className="bg-white border border-forest/10 rounded-2xl p-6 h-full">
                <t.icon className="w-7 h-7 text-terracotta" />
                <div className="font-display text-lg text-forest mt-3">{t.title}</div>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{t.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="text-center mt-6">
            <Link to="/safety" data-testid="about-safety-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-terracotta transition-colors">
              Read the full safety guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Contact + CTA */}
      <section className="max-w-4xl mx-auto px-6 py-14 text-center">
        <Reveal>
          <div className="bg-forest text-white rounded-3xl p-10">
            <h2 className="font-display text-3xl leading-tight tracking-tight">Questions, ideas, partnerships?</h2>
            <p className="text-white/80 mt-3">A real person reads every email.</p>
            <div className="flex flex-wrap gap-3 justify-center mt-6">
              <a href="mailto:hello@civichearth.com" data-testid="about-contact-btn">
                <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12">
                  <Mail className="w-4 h-4 mr-2" /> hello@civichearth.com
                </Button>
              </a>
              <Link to="/explore" data-testid="about-explore-btn">
                <Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12">
                  Start exploring
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
